import exifr from 'exifr'

export interface ExifData {
  /** ISO datetime string from EXIF DateTimeOriginal (or fallback tags), or null if not found */
  createdAt: string | null
  /** Camera make + model, e.g. "Apple iPhone 15 Pro", or null if not found */
  camera: string | null
  /** GPS coordinates, or null if not found */
  gps: { lat: number; lng: number } | null
}

/**
 * Extracts the recording date from an MP4/MOV file by parsing the `mvhd`
 * (movie header) box directly from the binary container.
 *
 * The creation_time field in `mvhd` is seconds since 1904-01-01 00:00:00 UTC
 * (the QuickTime/MP4 epoch). Version 0 boxes store it as a 32-bit integer;
 * version 1 boxes store it as a 64-bit integer.
 *
 * We read only the first 512 KB — the moov/mvhd box is always near the start
 * of a well-formed file (especially phone recordings).
 *
 * Returns an ISO string, or null if the atom is absent or the date is zero.
 */
async function extractVideoDate(file: File): Promise<string | null> {
  try {
    const READ_BYTES = 512 * 1024
    const slice = file.slice(0, READ_BYTES)
    const buffer = await slice.arrayBuffer()
    const view = new DataView(buffer)
    const bytes = new Uint8Array(buffer)
    const len = bytes.length

    // QuickTime epoch: seconds from 1904-01-01 to 1970-01-01
    const QT_EPOCH_OFFSET = 2082844800

    // Scan for the 4-byte ASCII sequence "mvhd"
    for (let i = 0; i < len - 24; i++) {
      if (
        bytes[i]     === 0x6d && // m
        bytes[i + 1] === 0x76 && // v
        bytes[i + 2] === 0x68 && // h
        bytes[i + 3] === 0x64    // d
      ) {
        const version = view.getUint8(i + 4)
        let secondsSince1904: number

        if (version === 1) {
          // 64-bit — read as two 32-bit halves (hi << 32 | lo).
          // Dates from modern phones fit in the lo word until year 2038+.
          const hi = view.getUint32(i + 8,  false)
          const lo = view.getUint32(i + 12, false)
          secondsSince1904 = hi * 0x100000000 + lo
        } else {
          // 32-bit (version 0)
          secondsSince1904 = view.getUint32(i + 8, false)
        }

        if (secondsSince1904 === 0) return null

        const unixSeconds = secondsSince1904 - QT_EPOCH_OFFSET
        const date = new Date(unixSeconds * 1000)
        if (isNaN(date.getTime())) return null

        // Sanity check: reject dates before 1990 or in the future —
        // likely a false-positive match on non-mvhd bytes.
        const year = date.getUTCFullYear()
        if (year < 1990 || date > new Date()) return null

        return date.toISOString()
      }
    }
    return null
  } catch {
    return null
  }
}

/**
 * Reads the first N bytes of a File into an ArrayBuffer once (shared by HEIC
 * and video extraction) to avoid reading the same file slice multiple times.
 */
async function readHead(file: File, bytes: number): Promise<ArrayBuffer> {
  return file.slice(0, bytes).arrayBuffer()
}

/**
 * Extracts EXIF metadata from a HEIC/HEIF file by scanning the ISOBMFF
 * container for the TIFF header signature present in the embedded Exif item.
 *
 * HEIC files store EXIF as an item (referenced via iinf/iloc) whose data is a
 * raw TIFF stream beginning with the standard TIFF byte-order marker (II or MM)
 * followed by the magic number 42. We scan the file's header area for this
 * 4-byte signature and feed the surrounding data to exifr.
 *
 * Returns the full ExifData or null if extraction fails.
 */
async function extractHeicExif(file: File): Promise<ExifData | null> {
  try {
    const READ_BYTES = 512 * 1024
    const buffer = await readHead(file, READ_BYTES)
    const bytes = new Uint8Array(buffer)
    const len = bytes.length

    const LE_SIG = new Uint8Array([0x49, 0x49, 0x2A, 0x00])
    const BE_SIG = new Uint8Array([0x4D, 0x4D, 0x00, 0x2A])

    for (let i = 0; i < len - 64; i++) {
      let le = true
      if (
        bytes[i] === LE_SIG[0] && bytes[i + 1] === LE_SIG[1] &&
        bytes[i + 2] === LE_SIG[2] && bytes[i + 3] === LE_SIG[3]
      ) {
        le = true
      } else if (
        bytes[i] === BE_SIG[0] && bytes[i + 1] === BE_SIG[1] &&
        bytes[i + 2] === BE_SIG[2] && bytes[i + 3] === BE_SIG[3]
      ) {
        le = false
      } else {
        continue
      }

      // Likely found a TIFF header. Sanity-check the IFD0 offset (bytes 4-7).
      const view = new DataView(buffer)
      const ifd0Off = view.getUint32(i + 4, le)
      if (ifd0Off < 8 || ifd0Off > 65536) continue

      // Extract a reasonable window for exifr to work with (TIFF/EXIF data
      // is typically small, but give it up to 64KB to be safe).
      const tiffEnd = Math.min(i + 65536, len)
      const tiffBuf = buffer.slice(i, tiffEnd)

      const tags = await exifr.parse(tiffBuf, {
        pick: ['DateTimeOriginal', 'DateTime', 'DateTimeDigitized', 'Make', 'Model', 'GPSLatitude', 'GPSLongitude', 'GPSLatitudeRef', 'GPSLongitudeRef'],
        gps: true,
      })
      if (!tags) continue

      const rawDate = tags.DateTimeOriginal ?? tags.DateTime ?? tags.DateTimeDigitized
      let createdAt = null
      if (rawDate) {
        const d = rawDate instanceof Date ? rawDate : new Date(String(rawDate))
        if (!isNaN(d.getTime())) createdAt = d.toISOString()
      }

      const parts = [tags.Make, tags.Model]
        .map((v: unknown) => (typeof v === 'string' ? v.trim() : ''))
        .filter(Boolean)
      const camera = parts.length === 2 && String(parts[1]).startsWith(String(parts[0]))
        ? String(parts[1])
        : parts.join(' ').trim() || null

      const gps = typeof tags.latitude === 'number' && typeof tags.longitude === 'number'
        ? { lat: tags.latitude, lng: tags.longitude }
        : null

      return { createdAt, camera, gps }
    }
  } catch {
    // EXIF parsing errors are non-fatal
  }
  return null
}

/**
 * Extracts EXIF metadata from a File before it is resized/re-encoded.
 * Must be called on the original file — canvas re-encoding in resizeImage()
 * strips all EXIF data as a side effect.
 *
 * Always resolves (never throws). Returns all-nulls for videos, screenshots,
 * and any file where EXIF data is absent or unreadable.
 */
export async function extractExif(file: File): Promise<ExifData> {
  // Videos don't have EXIF — extract date from the MP4/MOV container instead.
  // Also check by extension because file.type is "" for .mov files on desktop Chrome.
  const isVideo =
    file.type.startsWith('video/') ||
    /\.(mp4|mov|m4v|avi|mkv|webm)$/i.test(file.name)
  if (isVideo) {
    const createdAt = await extractVideoDate(file)
    return { createdAt, camera: null, gps: null }
  }

  // HEIC/HEIF files store EXIF in an ISOBMFF 'Exif' box (not in JPEG APP1
  // segments), so exifr's magic-byte detection may not parse them reliably.
  // Extract the embedded TIFF data manually and feed it to exifr.
  const isHeic = /\.(heic|heif)$/i.test(file.name)
  if (isHeic) {
    const result = await extractHeicExif(file)
    if (result) return result
  }

  try {
    const tags = await exifr.parse(file, {
      pick: [
        'DateTimeOriginal',
        'DateTime',
        'DateTimeDigitized',
        'Make',
        'Model',
        'GPSLatitude',
        'GPSLongitude',
        'GPSLatitudeRef',
        'GPSLongitudeRef',
      ],
      gps: true,
    })

    if (!tags) return { createdAt: null, camera: null, gps: null }

    // Date: prefer DateTimeOriginal > DateTime > DateTimeDigitized
    const rawDate: Date | string | undefined =
      tags.DateTimeOriginal ?? tags.DateTime ?? tags.DateTimeDigitized
    let createdAt: string | null = null
    if (rawDate) {
      const d = rawDate instanceof Date ? rawDate : new Date(rawDate as string)
      if (!isNaN(d.getTime())) {
        createdAt = d.toISOString()
      }
    }

    // Camera: "Apple iPhone 15 Pro" style
    const parts = [tags.Make, tags.Model]
      .map((v: unknown) => (typeof v === 'string' ? v.trim() : ''))
      .filter(Boolean)
    // Avoid duplicating make if model already contains it (e.g. "Apple" + "Apple iPhone 15")
    const camera =
      parts.length === 2 && (parts[1] as string).startsWith(parts[0] as string)
        ? (parts[1] as string)
        : parts.join(' ').trim() || null

    // GPS: exifr returns raw DMS arrays in GPSLatitude/GPSLongitude.
    // With gps:true it also produces synthetic latitude/longitude keys in
    // decimal degrees — read those instead.
    const gps =
      typeof tags.latitude === 'number' && typeof tags.longitude === 'number'
        ? { lat: tags.latitude, lng: tags.longitude }
        : null

    return { createdAt, camera, gps }
  } catch {
    return { createdAt: null, camera: null, gps: null }
  }
}
